import JSZip from 'jszip';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

// Helper to detect MIME type from base64 data
const detectMimeType = (base64: string): string => {
  if (base64.startsWith('/9j/')) return 'image/jpeg';
  if (base64.startsWith('iVBORw')) return 'image/png';
  if (base64.startsWith('R0lGOD')) return 'image/gif';
  if (base64.startsWith('UklGR')) return 'image/webp';
  return 'image/jpeg'; // Default to JPEG
};

// Helper to get file extension from MIME type
const getExtensionFromMime = (mimeType: string): string => {
  if (mimeType.includes('jpeg') || mimeType.includes('jpg')) return 'jpg';
  if (mimeType.includes('png')) return 'png';
  if (mimeType.includes('gif')) return 'gif';
  if (mimeType.includes('webp')) return 'webp';
  return 'jpg'; // Default to jpg
};

// Fetch image URL on demand from the public view (excludes sensitive fields like user_id, api_key_id)
const fetchImageData = async (imageId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('public_gallery_images')
      .select('image_url')
      .eq('id', imageId)
      .single();
    
    if (error) throw error;
    // Return the image_url which may be a data URL or regular URL
    return data?.image_url || null;
  } catch (error) {
    console.error('Failed to fetch image data:', error);
    return null;
  }
};

// Helper to get correct data URL from image
const getImageDataUrl = async (image: any): Promise<string | null> => {
  // First priority: If we have a valid image_url (not "base64" placeholder)
  if (image.image_url && 
      image.image_url !== 'base64' && 
      image.image_url.length > 10) {
    return image.image_url;
  }
  
  // Second: Fetch image_url on demand from the public view
  const fetchedUrl = await fetchImageData(image.id);
  if (fetchedUrl && fetchedUrl !== 'base64') {
    return fetchedUrl;
  }
  
  return null;
};

// Convert base64 to Blob properly
const base64ToBlob = (base64: string, mimeType: string): Blob => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
};

export const usePublicGalleryDownload = () => {
  const downloadImagesAsZip = async (images: any[], category: string) => {
    const toastId = toast.loading('Preparing images for download...');
    
    try {
      const zip = new JSZip();
      // Clean category name - remove number prefix and special chars
      const cleanCategory = category.replace(/^\d+[.\s-]+/, '').replace(/[–—]/g, '-').replace(/[^a-z0-9-_\s]/gi, '').trim() || 'gallery';
      const folder = zip.folder(`${cleanCategory}-gallery-images`);
      let successCount = 0;
      
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        
        try {
          let blob: Blob;
          let mimeType = 'image/jpeg';
          
          // Get image data (fetch if needed)
          const imageUrl = await getImageDataUrl(image);
          if (!imageUrl) {
            console.warn(`Skipping image ${i}: no data available`);
            continue;
          }
          
          if (imageUrl.startsWith('data:image/')) {
            const base64Data = imageUrl.split(',')[1];
            mimeType = imageUrl.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
            blob = base64ToBlob(base64Data, mimeType);
          } else {
            const response = await fetch(imageUrl, { mode: 'cors' });
            blob = await response.blob();
            mimeType = blob.type || 'image/jpeg';
          }
          
          const ext = getExtensionFromMime(mimeType);
          // Clean prompt for filename - remove number prefix and special chars
          const cleanPrompt = image.prompt.replace(/^\d+[.\s-]+/, '').replace(/[–—]/g, '-').slice(0, 30).replace(/[^a-z0-9]/gi, '_');
          const filename = `${cleanPrompt}-${image.id.slice(0, 8)}.${ext}`;
          folder?.file(filename, blob);
          successCount++;
          
          if (i % 10 === 0) {
            toast.loading(`Processing ${i + 1}/${images.length} images...`, { id: toastId });
          }
        } catch (error) {
          console.error(`Failed to process image ${i}:`, error);
        }
      }
      
      if (successCount === 0) {
        toast.error('No images could be downloaded', { id: toastId });
        return;
      }
      
      toast.loading('Creating ZIP file...', { id: toastId });
      
      // STORE compression = no compression, preserves quality
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'STORE' 
      });
      
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(zipBlob);
      link.download = `gallery-${cleanCategory}-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      
      toast.success(`Downloaded ${successCount} images successfully!`, { id: toastId });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download images', { id: toastId });
    }
  };

  const downloadSingleImage = async (image: any) => {
    const toastId = toast.loading('Preparing download...');
    
    try {
      let blob: Blob;
      let mimeType = 'image/jpeg';
      
      // Get image data (fetch if needed)
      const imageUrl = await getImageDataUrl(image);
      if (!imageUrl) {
        toast.error('No image data available', { id: toastId });
        return;
      }
      
      if (imageUrl.startsWith('data:image/')) {
        const base64Data = imageUrl.split(',')[1];
        mimeType = imageUrl.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
        blob = base64ToBlob(base64Data, mimeType);
      } else {
        const response = await fetch(imageUrl, { mode: 'cors' });
        blob = await response.blob();
        mimeType = blob.type || 'image/jpeg';
      }
      
      const ext = getExtensionFromMime(mimeType);
      // Clean prompt for filename - remove number prefix and special chars
      const cleanPrompt = image.prompt.replace(/^\d+[.\s-]+/, '').replace(/[–—]/g, '-').slice(0, 30).replace(/[^a-z0-9]/gi, '_');
      
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${cleanPrompt}-${image.id.slice(0, 8)}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      
      toast.success('Image downloaded successfully!', { id: toastId });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image', { id: toastId });
    }
  };
  
  return { downloadImagesAsZip, downloadSingleImage };
};
